import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../Config/axiosInstance';
import './EditPricePerHour.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat } from "react-number-format";

registerLocale("ro", ro);

const formatDateLabel = (dateString) => {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

function EditPricePerHour() {
  const navigate = useNavigate();
  const { id } = useParams();
  const animatedComponents = makeAnimated();

  const [formData, setFormData] = useState({
    price_per_hour: "",
    internal_resource: "",
    date: "",
    currency: "",
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [errors, setErrors] = useState({});

  const [internalResources, setInternalResources] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);
  const [systemPricePreview, setSystemPricePreview] = useState("");

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const map = new Map();

    for (const rate of exchangeRates) {
      const currencyId = rate.currency;

      if (!map.has(currencyId)) {
        map.set(currencyId, []);
      }

      map.get(currencyId).push({
        value: rate.id,
        label: String(rate.value ?? ""),
        dateLabel: rate.date || "",
        rateValue: Number(rate.value),
        rateDate: rate.date,
      });
    }

    for (const [currencyId, options] of map.entries()) {
      options.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      map.set(currencyId, options);
    }

    return map;
  }, [exchangeRates]);

  const getDefaultExchangeRateOption = useCallback(
    (currencyId) => {
      const options = exchangeOptionsByCurrencyId.get(currencyId) || [];
      return options.length ? options[0] : null;
    },
    [exchangeOptionsByCurrencyId]
  );

  const computeSystemPrice = useCallback(
    (price, currencyId, exchangeOpt) => {
      if (price === null || price === undefined || price === "") return "";

      const numericPrice = Number(price);
      if (!Number.isFinite(numericPrice)) return "";

      if (!currencyId || !systemCurrencyId) return "";

      if (Number(currencyId) === Number(systemCurrencyId)) {
        return numericPrice.toFixed(2);
      }

      const rateValue = exchangeOpt?.rateValue;
      if (!rateValue || !Number.isFinite(rateValue)) return "";

      return (numericPrice * rateValue).toFixed(2);
    },
    [systemCurrencyId]
  );

  const updateSystemPreview = useCallback(
    ({ price, currency, exchangeRate }) => {
      const currencyId = currency?.value ?? null;
      const result = computeSystemPrice(price, currencyId, exchangeRate);
      setSystemPricePreview(result);
    },
    [computeSystemPrice]
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [
          internalResourcesRes,
          currenciesRes,
          exchangeRatesRes,
          systemCurrencyRes,
          pricePerHourRes,
        ] = await Promise.all([
          axiosInstance.get('/internal-resources/'),
          axiosInstance.get('/currencies/'),
          axiosInstance.get('/exchange-rate/'),
          axiosInstance.get('/system-currency/'),
          axiosInstance.get(`/price-per-hour/${id}/`),
        ]);

        const formattedInternalResources = (internalResourcesRes.data || []).map(ir => ({
          value: ir.id,
          label: `${ir.resource_name}`,
        }));

        const formattedCurrencies = (currenciesRes.data || []).map(currency => ({
          value: currency.id,
          label: currency.abbreviation,
          abbreviation: currency.abbreviation,
          name: currency.name,
        }));

        const sortedExchangeRates = (exchangeRatesRes.data || []).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        setInternalResources(formattedInternalResources);
        setCurrencyOptions(formattedCurrencies);
        setExchangeRates(sortedExchangeRates);

        let currentSystemCurrencyId = null;
        let currentSystemCurrencyLabel = "";

        if (
          Array.isArray(systemCurrencyRes.data) &&
          systemCurrencyRes.data.length > 0
        ) {
          currentSystemCurrencyId = systemCurrencyRes.data[0]?.currency ?? null;

          const systemCurrency = formattedCurrencies.find(
            currency => Number(currency.value) === Number(currentSystemCurrencyId)
          );

          currentSystemCurrencyLabel = systemCurrency?.label || "";

          setSystemCurrencyId(currentSystemCurrencyId || null);
          setSystemCurrencyLabel(currentSystemCurrencyLabel);
        }

        const {
          price_per_hour,
          internal_resource,
          date,
          currency,
          exchange_rate,
        } = pricePerHourRes.data;

        const priceValue =
          price_per_hour === null || price_per_hour === undefined
            ? ""
            : String(price_per_hour);

        const currentCurrency = formattedCurrencies.find(
          option => Number(option.value) === Number(currency)
        ) || null;

        const ratesForCurrency = sortedExchangeRates
          .filter(rate => Number(rate.currency) === Number(currency))
          .map(rate => ({
            value: rate.id,
            label: String(rate.value ?? ""),
            dateLabel: rate.date || "",
            rateValue: Number(rate.value),
            rateDate: rate.date,
          }))
          .sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));

        let currentExchangeRate = null;

        if (
          currentCurrency &&
          currentSystemCurrencyId &&
          Number(currentCurrency.value) !== Number(currentSystemCurrencyId)
        ) {
          currentExchangeRate =
            ratesForCurrency.find(rate => Number(rate.value) === Number(exchange_rate)) ||
            ratesForCurrency[0] ||
            null;
        }

        setFormData({
          price_per_hour: priceValue,
          internal_resource: internal_resource || "",
          date: date || "",
          currency: currency || "",
        });

        setSelectedCurrency(currentCurrency);
        setSelectedExchangeRate(currentExchangeRate);

        if (date) setSelectedDate(new Date(date));

        if (
          priceValue &&
          currentCurrency &&
          currentSystemCurrencyId
        ) {
          if (Number(currentCurrency.value) === Number(currentSystemCurrencyId)) {
            setSystemPricePreview(Number(priceValue).toFixed(2));
          } else if (currentExchangeRate?.rateValue) {
            setSystemPricePreview((Number(priceValue) * currentExchangeRate.rateValue).toFixed(2));
          }
        }
      } catch (error) {
        console.error("Eroare la preluarea datelor pentru editare:", error);
      }
    };

    if (id) fetchInitialData();
  }, [id]);

  const handleDateChange = (date) => {
    const isoDate = date ? date.toISOString().split("T")[0] : "";

    setFormData(prev => ({
      ...prev,
      date: isoDate,
    }));

    setSelectedDate(date);

    if (errors.date) {
      setErrors(prev => ({ ...prev, date: "" }));
    }
  };

  const handlePriceChange = ({ value }) => {
    const nextPrice = value || "";

    setFormData(prev => ({
      ...prev,
      price_per_hour: nextPrice,
    }));

    if (errors.price_per_hour) {
      setErrors(prev => ({ ...prev, price_per_hour: "" }));
    }

    updateSystemPreview({
      price: nextPrice,
      currency: selectedCurrency,
      exchangeRate: selectedExchangeRate,
    });
  };

  const handleCurrencyChange = (selectedOption) => {
    setSelectedCurrency(selectedOption);

    const currencyId = selectedOption?.value ?? "";

    setFormData(prev => ({
      ...prev,
      currency: currencyId,
    }));

    let nextExchangeRate = null;

    if (
      currencyId &&
      systemCurrencyId &&
      Number(currencyId) !== Number(systemCurrencyId)
    ) {
      nextExchangeRate = getDefaultExchangeRateOption(currencyId);
    }

    setSelectedExchangeRate(nextExchangeRate);

    updateSystemPreview({
      price: formData.price_per_hour,
      currency: selectedOption,
      exchangeRate: nextExchangeRate,
    });

    if (errors.currency) {
      setErrors(prev => ({ ...prev, currency: "" }));
    }

    if (errors.exchange_rate) {
      setErrors(prev => ({ ...prev, exchange_rate: "" }));
    }
  };

  const handleExchangeRateChange = (selectedOption) => {
    setSelectedExchangeRate(selectedOption);

    updateSystemPreview({
      price: formData.price_per_hour,
      currency: selectedCurrency,
      exchangeRate: selectedOption,
    });

    if (errors.exchange_rate) {
      setErrors(prev => ({ ...prev, exchange_rate: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.price_per_hour.trim()) {
      newErrors.price_per_hour = "Please fill out this field";
    } else if (
      isNaN(formData.price_per_hour) ||
      Number(formData.price_per_hour) <= 0
    ) {
      newErrors.price_per_hour = "Please enter a valid positive number";
    }

    if (!formData.internal_resource) {
      newErrors.internal_resource = "Please fill out this field";
    }

    if (!formData.date) {
      newErrors.date = "Please fill out this field";
    }

    if (!selectedCurrency) {
      newErrors.currency = "Please fill out this field";
    }

    if (
      selectedCurrency &&
      systemCurrencyId &&
      Number(selectedCurrency.value) !== Number(systemCurrencyId) &&
      !selectedExchangeRate
    ) {
      newErrors.exchange_rate = "Please select an exchange rate";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        price_per_hour:
          formData.price_per_hour === ""
            ? ""
            : Number(formData.price_per_hour),
        currency: selectedCurrency.value,
      };

      if (
        selectedCurrency &&
        systemCurrencyId &&
        Number(selectedCurrency.value) === Number(systemCurrencyId)
      ) {
        payload.exchange_rate = null;
      } else {
        payload.exchange_rate = selectedExchangeRate ? selectedExchangeRate.value : null;
      }

      const response = await axiosInstance.put(`/price-per-hour/${id}/`, payload);

      console.log("Updated successfully:", response.data);
      navigate(-1);
    } catch (error) {
      console.error("Error updating:", error.response?.data || error.message);
    }
  };

  const exchangeOptions = selectedCurrency
    ? exchangeOptionsByCurrencyId.get(selectedCurrency.value) || []
    : [];

  const selectedCurrencyIsSystem =
    selectedCurrency &&
    systemCurrencyId &&
    Number(selectedCurrency.value) === Number(systemCurrencyId);

  return (
    <div className="edit-price-page">
      <div className="edit-container">
        <div className="header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="edit-title">Edit Price/Hour</h2>
        </div>
      </div>

      <hr className="title-separator" />

      <div className="edit-price-container">
        <div className="edit-container">
          <form onSubmit={handleSubmit} className="edit-form" noValidate>

            <div className="form-group">
              <label htmlFor="price_per_hour" className="label">
                Price/Hour <span style={{ color: 'red' }}>*</span>
              </label>

              <NumericFormat
                id="price_per_hour"
                name="price_per_hour"
                value={formData.price_per_hour}
                onValueChange={handlePriceChange}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                allowLeadingZeros={false}
                inputMode="decimal"
                placeholder="0,00"
                className={`input ${errors.price_per_hour ? 'input-error' : ''}`}
                style={{
                  borderColor: errors.price_per_hour ? '#ff0000' : '',
                  borderWidth: errors.price_per_hour ? '2px' : '1px'
                }}
                required
              />

              {errors.price_per_hour && (
                <div className="error-text" style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                  {errors.price_per_hour}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="label">
                Currency <span style={{ color: 'red' }}>*</span>
              </label>

              <Select
                id="currency"
                name="currency"
                classNamePrefix="react-select"
                options={currencyOptions}
                value={selectedCurrency}
                onChange={handleCurrencyChange}
                components={animatedComponents}
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: errors.currency ? '#ff0000' : provided.borderColor,
                    borderWidth: errors.currency ? '2px' : '1px',
                    '&:hover': {
                      borderColor: errors.currency ? '#ff0000' : provided.borderColor,
                    },
                  }),
                }}
              />

              {errors.currency && (
                <div className="error-text" style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                  {errors.currency}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="exchange_rate" className="label">
                Exchange Rate
              </label>

              <Select
                inputId="exchange_rate"
                classNamePrefix="react-select"
                placeholder={
                  !selectedCurrency
                    ? "Select currency first"
                    : selectedCurrencyIsSystem
                      ? "Not needed"
                      : exchangeOptions.length
                        ? "Select rate"
                        : "No rates"
                }
                isSearchable
                isClearable
                isDisabled={!selectedCurrency || selectedCurrencyIsSystem}
                options={exchangeOptions}
                value={selectedExchangeRate}
                onChange={handleExchangeRateChange}
                components={animatedComponents}
                formatOptionLabel={(option, { context }) =>
                  context === "value"
                    ? option.label
                    : `${option.label}${option.dateLabel ? ` (${formatDateLabel(option.dateLabel)})` : ""}`
                }
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: errors.exchange_rate ? '#ff0000' : provided.borderColor,
                    borderWidth: errors.exchange_rate ? '2px' : '1px',
                    '&:hover': {
                      borderColor: errors.exchange_rate ? '#ff0000' : provided.borderColor,
                    },
                  }),
                }}
              />

              {errors.exchange_rate && (
                <div className="error-text" style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                  {errors.exchange_rate}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="price_per_hour_system" className="label">
                Price/Hour System
                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
              </label>

              <NumericFormat
                id="price_per_hour_system"
                name="price_per_hour_system"
                value={systemPricePreview}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="internal_resource" className="label">
                Internal Resource <span style={{ color: 'red' }}>*</span>
              </label>

              <Select
                id="internal_resource"
                name="internal_resource"
                classNamePrefix="react-select"
                options={internalResources}
                value={internalResources.find((option) => option.value === formData.internal_resource) || null}
                onChange={(selectedOption) => {
                  const newValue = selectedOption ? selectedOption.value : "";
                  setFormData(prev => ({ ...prev, internal_resource: newValue }));

                  if (errors.internal_resource) {
                    setErrors(prev => ({ ...prev, internal_resource: "" }));
                  }
                }}
                components={animatedComponents}
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: errors.internal_resource ? '#ff0000' : provided.borderColor,
                    borderWidth: errors.internal_resource ? '2px' : '1px',
                    '&:hover': {
                      borderColor: errors.internal_resource ? '#ff0000' : provided.borderColor,
                    },
                  }),
                }}
              />

              {errors.internal_resource && (
                <div className="error-text" style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                  {errors.internal_resource}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="date" className="label">
                Date <span style={{ color: 'red' }}>*</span>
              </label>

              <div className="datepicker-container">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  placeholderText="dd.mm.yyyy"
                  className={`datepicker-input ${errors.date ? 'input-error' : ''}`}
                  wrapperClassName="datepicker-wrapper"
                />

                <span className="calendar-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>

              {errors.date && (
                <div className="error-text" style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                  {errors.date}
                </div>
              )}
            </div>

            <div className="form-group-row">
              <div className="button-group-inline">
                <button type="button" className="button discard" onClick={() => navigate(-1)}>
                  Discard
                </button>
                <button type="submit" className="button save">Save</button>
              </div>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default EditPricePerHour;